import type { Session } from '../types'

/**
 * Zentrales brand-admin-Scoping (Single-Source). Bündelt die früher verstreuten
 * Ternaries aus den Stores (`vcard/account/brandStore`) und `permissions.ts`.
 *
 * Semantik (exakt wie zuvor):
 * - admin       → sieht alles; `scopedBrandParam()` = undefined; `assertBrandAccess` = true.
 * - brand-admin → auf `brandKey` beschränkt; `scopedBrandParam()` = brandKey;
 *                 `assertBrandAccess(bk)` = bk === brandKey.
 * - user/anon   → kein Admin-Zugriff; kein Scope-Param; kein Brand-Zugriff.
 *
 * Reine Funktion (auch außerhalb React nutzbar/testbar). Der React-Zugriff läuft
 * über `useBrandScope()` (dünner `useAuth()`-Wrapper).
 *
 * WICHTIG: Frontend-Scoping ist reine UX/Defense-in-depth — die serverseitige
 * Autorisierung bleibt maßgeblich und wird nicht umgangen.
 */
export interface BrandScope {
  /** Rolle admin (sieht alles). */
  isAdmin: boolean
  /** Rolle brand-admin (auf eigene Brand beschränkt). */
  isBrandAdmin: boolean
  /** Eigene Brand (brand-admin/user) bzw. null (admin/anon). */
  brandKey: string | null
  /** admin sieht die vollständige Brand-Übersicht. */
  canSeeAllBrands: boolean
  /** Listen-Filter-Parameter: brand-admin = eigene Brand, sonst undefined (= keine Filterung). */
  scopedBrandParam: () => string | undefined
  /** Darf die Session auf die Brand `bk` zugreifen? */
  assertBrandAccess: (bk: string) => boolean
}

export function brandScope(session: Session | null | undefined): BrandScope {
  const isAdmin = session?.role === 'admin'
  const isBrandAdmin = session?.role === 'brand-admin'
  const brandKey = isBrandAdmin ? (session?.brandKey ?? null) : null

  return {
    isAdmin,
    isBrandAdmin,
    brandKey,
    canSeeAllBrands: isAdmin,
    scopedBrandParam: () => (isBrandAdmin ? (session?.brandKey ?? undefined) : undefined),
    assertBrandAccess: (bk: string) => isAdmin || (isBrandAdmin && session?.brandKey === bk),
  }
}
